import { useState } from 'react';
import { Autocomplete, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import { Alert, FlexColumn, TextField } from '@elementary/common-ui/components';
import useSnackbar from 'shared/hooks/useSnackbar';
import useAlertDestinations from 'shared/hooks/useAlertDestinations';
import * as api from 'shared/services/settingsApi';
import TestMessageSection from './TestMessageSection';

type Props = {
  onSuccess?: () => void;
  onReset?: () => void;
};

const TeamsBotTestSection = ({ onSuccess, onReset }: Props) => {
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [customMessage, setCustomMessage] = useState<string>('');

  const { openSnackbar } = useSnackbar();
  const { alertDestinationOptions } = useAlertDestinations();
  const channels = alertDestinationOptions?.microsoft_teams_bot?.options ?? [];

  const defaultMessage =
    'This is a test message from Elementary Data to verify your Microsoft Teams app integration is working correctly.';

  const testMutation = useMutation(
    async () =>
      api.sendTeamsBotTestMessage(
        selectedChannel,
        customMessage.trim() || undefined,
      ),
    {
      onSuccess: (response) => {
        if (response.success) {
          openSnackbar({
            message: 'Test message sent successfully to Teams!',
            variant: 'success',
          });
          onSuccess?.();
        } else {
          openSnackbar({
            message: response.message || 'Failed to send test message to Teams',
            variant: 'error',
          });
          onReset?.();
        }
      },
      onError: (error: MutationError) => {
        openSnackbar({
          message:
            error?.message ||
            error?.response?.data?.message ||
            'Failed to send test message to Teams',
          variant: 'error',
        });
      },
    },
  );

  const handleSendTest = () => {
    if (!selectedChannel) return;
    testMutation.mutate();
  };

  const selectedOption =
    channels.find((c) => c.value === selectedChannel) || null;

  return (
    <FlexColumn sx={{ gap: 3 }}>
      <Typography>
        Send a test message to verify your Microsoft Teams app integration is
        working correctly.
      </Typography>

      {channels.length === 0 && (
        <Alert severity="info">
          No channels available. Make sure the app is installed and connected.
        </Alert>
      )}

      <Autocomplete
        options={channels}
        value={selectedOption}
        onChange={(_, newValue) => setSelectedChannel(newValue?.value || '')}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        disabled={testMutation.isLoading}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Teams channel"
            placeholder="e.g., Team / General"
            fullWidth
            size="large"
          />
        )}
      />

      <TestMessageSection
        customMessage={customMessage}
        onCustomMessageChange={setCustomMessage}
        defaultMessage={defaultMessage}
        onSendTest={handleSendTest}
        isLoading={testMutation.isLoading}
        disabled={!selectedChannel}
      />
    </FlexColumn>
  );
};

export default TeamsBotTestSection;
